import React, { useEffect } from 'react'

import { type InfiniteData } from '@tanstack/react-query'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Users } from '@/components/common/Avatar'
import Button from '@/components/common/Button'
import Checkbox from '@/components/common/Checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { ToggleAllButton } from '@/components/form/Buttons'
import Label from '@/components/form/Label'
import TextInput from '@/components/form/TextInput'
import { LoadingComponent } from '@/components/profile/Loading'

import {
  useBillingOrganization,
  useCanInviteMembers,
  useMaximumUsersReached,
} from '@/hooks/billing/useBillingOrganization'
import { useBillingRole } from '@/hooks/billing/useBillingRole'
import { useInviteToOrganization } from '@/hooks/billing/useInviteToOrganization'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizations } from '@/hooks/organization/useOrganizations'

import { PaginatedResponse } from '@/types/Api'
import { UUID } from '@/types/Id'
import { Organization } from '@/types/Organization'

interface InviteFormValues {
  inviteeEmail: string
  billingRole: string
  targetOrgs: UUID[]
}

interface InviteToOrganizationModalProps {
  isOpen: boolean
  autoSelectOrg?: UUID
  onClose: () => void
  onInviteSuccess?: () => void
}

const selectOrgs = (data: InfiniteData<PaginatedResponse<Organization>>) => {
  return data.pages
    .flatMap((page) => page.data)
    .sort((a, b) => a.name.localeCompare(b.name))
}

export const getAvailableRoles = (billingRole: string | undefined) => {
  switch (billingRole) {
    case 'Owner':
      return ['Owner', 'Admin', 'Member'] //, 'Guest']
    case 'Admin':
      return ['Admin', 'Member'] //, 'Guest']
    default:
      return []
  }
}

/**
 * Roles a caller can act on (change or remove). Unlike assignable target roles,
 * this includes Guest — existing guests can be managed even though promoting or
 * inviting someone to Guest isn't offered. A caller who can't assign any role
 * can't manage members either.
 */
export const getManageableRoles = (billingRole: string | undefined) => {
  const assignable = getAvailableRoles(billingRole)
  return assignable.length > 0 ? [...assignable, 'Guest'] : assignable
}

export const InviteToOrganizationModal: React.FC<
  InviteToOrganizationModalProps
> = ({ isOpen, autoSelectOrg, onClose, onInviteSuccess }) => {
  const { activeOrgId } = useActiveOrgId()

  const { data: billingOrganization } = useBillingOrganization()

  const canInviteMembers = useCanInviteMembers()
  const maximumUsersReached = useMaximumUsersReached()

  const {
    data: organizations,
    isLoading: isLoadingOrgs,
    error: fetchOrgsError,
  } = useOrganizations({ allOrgs: true, select: selectOrgs })

  const billingRole = useBillingRole()

  const { mutateAsync: sendInvite, isPending: isSendingInvite } =
    useInviteToOrganization()

  const form = useForm<InviteFormValues>({
    defaultValues: {
      inviteeEmail: '',
      billingRole: 'Member',
      targetOrgs: [],
    },
    mode: 'onChange',
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = form

  const availableRoles = getAvailableRoles(billingRole)

  const handleClose = () => {
    onClose()
    reset()
  }

  const onSubmit = async (data: InviteFormValues) => {
    const selectedTargets = data.targetOrgs.map((id) => ({
      organization_id: id,
    }))

    const payload = {
      invitee_email: data.inviteeEmail,
      billing_role: data.billingRole,
      target_organizations: selectedTargets,
    }

    if (!billingOrganization || !canInviteMembers || maximumUsersReached) {
      return
    }

    try {
      await sendInvite(
        { billingOrgId: billingOrganization.id as UUID, inviteData: payload },
        {
          onSuccess: (_) => {
            handleClose()
            onInviteSuccess?.()
          },
        },
      )
    } catch (error) {
      console.error(error)
    }
  }

  // Watch both targetOrgs and inviteeEmail
  const targetOrgsWatch = watch('targetOrgs')
  const inviteeEmailWatch = watch('inviteeEmail')

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose()
    }
  }

  useEffect(() => {
    // automatically select the user's active org
    if (isOpen && activeOrgId) {
      setValue('targetOrgs', [activeOrgId])
    }
  }, [isOpen, activeOrgId])

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="space-y-4 md:w-[600px]"
        closeButtonClassName="top-0 right-3"
      >
        <DialogHeader>
          <DialogTitle>Invite User to {billingOrganization?.name}</DialogTitle>
          <DialogDescription>
            Enter their email address and assign a role for the new member.
          </DialogDescription>
        </DialogHeader>

        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 pt-2">
            {/* Invitee Email Input */}
            <Controller
              name="inviteeEmail"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
                validate: (value) => {
                  if (!value?.trim()) return 'Email is required'
                  return true
                },
              }}
              render={({ field: { value, onChange, ...field } }) => (
                <div className="flex flex-col gap-2">
                  <Label>Invitee Email</Label>
                  <TextInput
                    {...field}
                    value={value || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(e.target.value.trim())
                    }}
                    label="Invitee Email"
                    placeholder="user@example.com"
                    error={errors.inviteeEmail?.message}
                    disabled={isSubmitting || isSendingInvite}
                    required
                  />
                </div>
              )}
            />

            {/* Billing Role Selection */}
            <Controller
              name="billingRole"
              control={control}
              rules={{ required: 'Billing role is required' }}
              render={({ field, fieldState: { error } }) => (
                <div className="flex flex-col gap-2">
                  <Label>
                    Role in{' '}
                    <span className="font-semibold">
                      {billingOrganization?.name}
                    </span>{' '}
                    <span className="font-normal text-primary-600 dark:text-primary-400">
                      (Billing Organization)
                    </span>
                  </Label>
                  <div className="flex flex-col gap-1">
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isSubmitting || isSendingInvite}
                    >
                      <SelectTrigger
                        id="billingRole"
                        className="w-full border-primary-300 shadow-sm"
                      >
                        <SelectValue>{field.value}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {availableRoles.map((role) => (
                          <SelectItem key={role} value={role}>
                            {role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-xs text-primary-500 dark:text-primary-400">
                      This role determines the user&apos;s permissions in
                      Sweetspot.
                    </p>
                    {error && (
                      <p className="text-sm text-red-600 mt-1">
                        {error.message}
                      </p>
                    )}
                  </div>
                </div>
              )}
            />

            {/* Controller for the array of target orgs */}
            <Controller
              name="targetOrgs"
              control={control}
              render={({ field }) => {
                const handleToggleAll = () => {
                  if (field.value.length > 0) {
                    field.onChange([])
                  } else {
                    field.onChange(organizations?.map((org) => org.id))
                  }
                }
                return (
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium text-primary-900 dark:text-primary-100">
                      Organization Access
                    </p>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-primary-500 dark:text-primary-400">
                        Select which organizations this user should have access
                        to.
                      </p>
                      <ToggleAllButton
                        name="targetOrgs"
                        availableValues={
                          organizations?.map((org) => org.id) || []
                        }
                      />
                    </div>

                    {isLoadingOrgs ? (
                      <LoadingComponent />
                    ) : fetchOrgsError ? (
                      <p className="text-sm text-red-600 dark:text-red-500">
                        Could not load organizations: {fetchOrgsError.message}
                      </p>
                    ) : organizations && organizations.length === 0 ? (
                      <p className="text-sm text-primary-500 dark:text-primary-400 italic">
                        No organizations found.
                      </p>
                    ) : (
                      <div className="rounded-md border border-primary-200 dark:border-primary-800">
                        <div>
                          <div className="divide-y divide-primary-200 dark:divide-primary-800">
                            {organizations?.map((org) => {
                              return (
                                <div
                                  key={org.id}
                                  className="flex items-center space-x-4 px-4 py-3 hover:bg-primary-50 dark:hover:bg-primary-800/50 first:rounded-t-md last:rounded-b-md"
                                >
                                  <div className="flex min-w-0 flex-1 items-center space-x-4">
                                    <Checkbox
                                      id={`org-select-${org.id}`}
                                      checked={field.value.includes(org.id)}
                                      onChange={(
                                        event: React.ChangeEvent<HTMLInputElement>,
                                      ) => {
                                        // add or remove the org.id from the field.value
                                        if (field.value.includes(org.id)) {
                                          field.onChange(
                                            field.value.filter(
                                              (id) => id !== org.id,
                                            ),
                                          )
                                        } else {
                                          field.onChange([
                                            ...field.value,
                                            org.id,
                                          ])
                                        }
                                      }}
                                      disabled={isSubmitting || isSendingInvite}
                                      childrenOnly={true}
                                    />
                                    <div className="flex items-center justify-between gap-2 w-full">
                                      <Label
                                        htmlFor={`org-select-${org.id}`}
                                        className="text-sm font-normal text-primary-900 dark:text-primary-100"
                                      >
                                        {org.name}
                                      </Label>
                                      <Users
                                        userIds={org.users.map(
                                          (user) => user.id as UUID,
                                        )}
                                        className="-space-x-1 justify-end"
                                        limit={5}
                                        width={26}
                                        height={26}
                                        animationDisabled={true}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              }}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline-compact"
                onClick={handleClose}
                disabled={isSubmitting || isSendingInvite}
              >
                Cancel
              </Button>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <Button
                    type="button"
                    variant="primary-compact"
                    isLoading={isSubmitting || isSendingInvite}
                    disabled={
                      isSubmitting ||
                      isSendingInvite ||
                      !inviteeEmailWatch?.trim() ||
                      !!errors.inviteeEmail ||
                      !!errors.billingRole ||
                      targetOrgsWatch.length === 0
                    }
                    onClick={handleSubmit(onSubmit)}
                  >
                    Send Invite
                  </Button>
                </TooltipTrigger>
                {!inviteeEmailWatch?.trim() || errors.inviteeEmail ? (
                  <TooltipContent className="max-w-72">
                    {errors.inviteeEmail?.message ||
                      'Please enter an email address'}
                  </TooltipContent>
                ) : targetOrgsWatch.length === 0 && !errors.billingRole ? (
                  <TooltipContent className="max-w-72">
                    You must select at least one organization.
                  </TooltipContent>
                ) : maximumUsersReached ? (
                  <TooltipContent className="max-w-72">
                    You have reached the maximum number of users for your
                    organization. Please contact support for assistance.
                  </TooltipContent>
                ) : null}
              </Tooltip>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

interface MaxUsersReachedTooltipProps {
  children: React.ReactNode
}

export const MaxUsersReachedTooltip: React.FC<MaxUsersReachedTooltipProps> = ({
  children,
}) => {
  const maximumUsersReached = useMaximumUsersReached()
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild={true}>{children}</TooltipTrigger>
      {maximumUsersReached ? (
        <TooltipContent className="max-w-72">
          You have reached the maximum number of users for your organization.
          Please contact support for assistance.
        </TooltipContent>
      ) : null}
    </Tooltip>
  )
}
